import { DesignEditorState, EditorDocument } from '../../modules/design-builder/design-editor-state';
import { SCREEN_COMPONENT_CATALOG } from '../rendering/component-registry';
import { isJsonValue } from '../../modules/design-builder/config-editor/config-value.utils';
import { FEATURE_VIEW_DEFINITIONS } from '../features/feature-registry';
import { LayoutItem } from '../layout/layout.model';
import { deserializeScreen } from './screen-schema';

export interface ScreenCatalogEntry {
  slug: string;
  title: string;
  accessKey: string;
  icon: string;
}
export interface PublishedScreen {
  schemaVersion: 1;
  slug: string;
  title: string;
  accessKey: string;
  icon: string;
  document: EditorDocument;
}
export const SCREEN_ASSETS = {
  foundry: 'assets/foundry-screens/',
  custom: 'assets/custom/',
} as const;
export const SCREEN_SLUG = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
export const RESERVED_SLUGS = new Set(['design-builder', 'session-expired', 'screens', 'assets', 'api']);

/** Raw editor exports use the URL's metadata defaults; full screen files carry their own permissions. */
export function readPublishedScreen(value: unknown, entry: ScreenCatalogEntry): PublishedScreen {
  if((value as any)?.version===2){const properties=(value as any).screenProperties;value={schemaVersion:1,slug:properties?.slug??entry.slug,title:properties?.title??properties?.name??entry.title,accessKey:properties?.accessKey??entry.accessKey,icon:properties?.icon??entry.icon,document:deserializeScreen(value)};}
  const candidate = value as Partial<PublishedScreen & EditorDocument> | null;
  const documentExport = candidate && candidate.schemaVersion === undefined && candidate.document === undefined
    && candidate.version === 1 && Array.isArray(candidate.pages);
  const screen = validatePublishedScreen(documentExport ? {
    schemaVersion: 1, slug: entry.slug, title: entry.title, accessKey: entry.accessKey,
    icon: entry.icon, document: value,
  } : value);
  if (screen.slug !== entry.slug) throw new Error('Screen metadata does not match its URL.');
  return screen;
}

export function validatePublishedScreen(value: unknown): PublishedScreen {
  if((value as any)?.version===2){const properties=(value as any).screenProperties;value={schemaVersion:1,slug:properties?.slug,title:properties?.title??properties?.name,accessKey:properties?.accessKey,icon:properties?.icon??'dashboard',document:deserializeScreen(value)};}
  const screen = value as PublishedScreen;
  if (!screen || screen.schemaVersion !== 1 || !SCREEN_SLUG.test(screen.slug) || screen.slug.length > 80 || RESERVED_SLUGS.has(screen.slug)
    || typeof screen.title !== 'string' || !screen.title.trim() || screen.title.length > 100
    || !SCREEN_SLUG.test(screen.accessKey) || typeof screen.icon !== 'string' || screen.icon.length > 80 || !isJsonValue(screen.document)) {
    throw new Error('Invalid published screen metadata.');
  }
  const state = new DesignEditorState();
  if (!state.restore(JSON.stringify(screen.document))) throw new Error('Invalid screen document.');
  const features = new Map(screen.document.features?.map(feature => [feature.id, feature]));
  if (features.size !== (screen.document.features?.length ?? 0)) throw new Error('Duplicate behavior ID.');
  for (const feature of features.values()) if (!FEATURE_VIEW_DEFINITIONS[feature.type] || !isJsonValue(feature.config)) throw new Error('Unsupported behavior definition.');
  for (const page of screen.document.pages) {
    if (page.source) throw new Error('This legacy source document must be migrated before publishing.');
    const placementIds = new Set<string>();
    const checkLayout = (item: LayoutItem, depth = 0): void => {
      if (!item || depth > 30 || typeof item.id !== 'string' || placementIds.has(item.id)) throw new Error('Invalid or duplicate layout item.');
      placementIds.add(item.id);
      if (item.nodeId && !page.nodes.some(node => node.id === item.nodeId)) throw new Error('Layout references a missing component.');
      for (const child of item.children ?? []) checkLayout(child, depth + 1);
    };
    if (page.layout) checkLayout(page.layout);
    for (const node of page.nodes) {
      const view = node.componentId?.startsWith('view:') ? node.componentId.slice(5).split('/') : undefined;
      const feature = view ? features.get(view[0]) : undefined;
      const knownView = !!view && !!feature && FEATURE_VIEW_DEFINITIONS[feature.type]?.views.includes(view[1]);
      if (node.kind === 'component' && !knownView && !SCREEN_COMPONENT_CATALOG.some(entry => entry.id === node.componentId)) {
        throw new Error(`Unsupported component: ${node.componentId}`);
      }
    }
  }
  return { ...screen, document: state.document() };
}
